import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import {
  Wrench,
  ClipboardList,
  FlaskConical,
  Package,
  DollarSign,
  Thermometer,
  BarChart3,
  Brain,
  QrCode,
  Bell,
  Building2,
  ShieldCheck,
  CheckCircle2,
  ArrowRight,
  Mail,
  Rocket,
  Activity,
  Calendar,
  FileText,
  Users,
  Zap,
  TrendingUp,
  ChevronDown,
  Globe,
  Lock,
  Star,
  Stethoscope,
  Factory,
  Microscope,
  HeartPulse,
} from "lucide-react";

/* ─── helpers ─── */
function useCountUp(target, duration = 2000, trigger) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!trigger) return;
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [trigger, target, duration]);
  return count;
}

function StatCard({ value, suffix = "", label, trigger }) {
  const n = useCountUp(value, 1800, trigger);
  return (
    <div className="text-center">
      <div className="text-4xl md:text-5xl font-extrabold text-cyan-400">
        {n.toLocaleString()}{suffix}
      </div>
      <div className="mt-2 text-gray-400 text-sm md:text-base">{label}</div>
    </div>
  );
}

/* ─── data ─── */
const modules = [
  {
    icon: <Wrench className="w-7 h-7" />,
    color: "from-blue-500 to-cyan-500",
    badge: "Core",
    title: "Gestión de Mantenimiento",
    desc: "Cronogramas preventivos, correctivos y predictivos. Órdenes de trabajo automatizadas con trazabilidad completa.",
    tags: ["Preventivo", "Correctivo", "OT Digitales"],
  },
  {
    icon: <ClipboardList className="w-7 h-7" />,
    color: "from-violet-500 to-purple-600",
    badge: "Soporte",
    title: "Gestión de Servicios",
    desc: "Control total de servicios y soporte técnico: asignación, seguimiento en tiempo real y cierre documentado.",
    tags: ["Tickets", "SLA", "Cierre digital"],
  },
  {
    icon: <FlaskConical className="w-7 h-7" />,
    color: "from-emerald-500 to-teal-500",
    badge: "Compliance",
    title: "Calibraciones & Metrología",
    desc: "Plantillas de calibración por tipo de equipo, patrones trazables, certificados digitales y control ONAC.",
    tags: ["Certificados", "ONAC", "Trazabilidad"],
  },
  {
    icon: <Package className="w-7 h-7" />,
    color: "from-orange-500 to-amber-500",
    badge: "Inventario",
    title: "Stock & Almacenes",
    desc: "Gestión multi-almacén con alertas de stock mínimo, entradas/salidas y trazabilidad por equipo.",
    tags: ["Multi-almacén", "Alertas", "Repuestos"],
  },
  {
    icon: <DollarSign className="w-7 h-7" />,
    color: "from-pink-500 to-rose-500",
    badge: "Finanzas",
    title: "Costos & TCO",
    desc: "Calcula el Costo Total de Propiedad por equipo, cliente o sede. Análisis de rentabilidad por intervención.",
    tags: ["TCO", "ROI", "Presupuesto"],
  },
  {
    icon: <Thermometer className="w-7 h-7" />,
    color: "from-cyan-500 to-sky-500",
    badge: "IoT",
    title: "Sensores Ambientales",
    desc: "Monitoreo en tiempo real de temperatura y humedad vía Tuya IoT. Alertas automáticas ante desviaciones.",
    tags: ["Temperatura", "Humedad", "Tiempo real"],
  },
  {
    icon: <BarChart3 className="w-7 h-7" />,
    color: "from-yellow-500 to-orange-400",
    badge: "Analytics",
    title: "Reportes & Analítica",
    desc: "Dashboards personalizables, KPIs de mantenimiento, exportación a PDF/Excel e indicadores estratégicos.",
    tags: ["KPIs", "PDF/Excel", "Dashboards"],
  },
  {
    icon: <Brain className="w-7 h-7" />,
    color: "from-purple-600 to-indigo-600",
    badge: "IA",
    title: "Inteligencia Artificial",
    desc: "Diagnóstico asistido por IA, análisis predictivo de fallas y recomendaciones automáticas de mantenimiento.",
    tags: ["Predictivo", "Diagnóstico", "OpenAI"],
  },
  {
    icon: <QrCode className="w-7 h-7" />,
    color: "from-lime-500 to-green-500",
    badge: "Digital",
    title: "QR & Trazabilidad",
    desc: "Escaneo QR por equipo para acceder al historial completo, generar órdenes y registrar intervenciones.",
    tags: ["Código QR", "Historial", "Móvil"],
  },
  {
    icon: <Bell className="w-7 h-7" />,
    color: "from-red-500 to-pink-500",
    badge: "Notificaciones",
    title: "Alertas & Push",
    desc: "Notificaciones push en tiempo real para vencimientos, alarmas críticas y asignación de órdenes.",
    tags: ["FCM", "Push", "Email"],
  },
  {
    icon: <Building2 className="w-7 h-7" />,
    color: "from-slate-500 to-gray-600",
    badge: "Multi-tenant",
    title: "Multi-empresa & Sedes",
    desc: "Arquitectura multi-tenant con aislamiento por empresa. Gestiona múltiples clientes y sedes desde una sola plataforma.",
    tags: ["Multi-sede", "Roles", "Permisos"],
  },
  {
    icon: <Lock className="w-7 h-7" />,
    color: "from-teal-500 to-emerald-600",
    badge: "Seguridad",
    title: "RBAC & Permisos",
    desc: "Control granular de roles y permisos por módulo. Auditoría completa de accesos y cambios.",
    tags: ["JWT", "Roles", "Auditoría"],
  },
];

const complianceFeatures = [
  {
    icon: <ShieldCheck className="w-6 h-6 text-emerald-400" />,
    title: "Resolución 3100 / Habilitación",
    desc: "Cumple los estándares de habilitación del MSPS para equipos biomédicos en IPS.",
  },
  {
    icon: <Microscope className="w-6 h-6 text-cyan-400" />,
    title: "Trazabilidad metrológica ONAC",
    desc: "Cadena de trazabilidad documentada con patrones calibrados y certificados validados.",
  },
  {
    icon: <FileText className="w-6 h-6 text-blue-400" />,
    title: "Historial técnico completo",
    desc: "Cada equipo cuenta con hoja de vida digital accesible en cualquier momento para auditorías.",
  },
  {
    icon: <Calendar className="w-6 h-6 text-violet-400" />,
    title: "Cronogramas regulatorios",
    desc: "Programación automática alineada a exigencias legales y frecuencias de mantenimiento normativas.",
  },
  {
    icon: <Activity className="w-6 h-6 text-pink-400" />,
    title: "Monitoreo continuo IoT",
    desc: "Registro automático de condiciones ambientales para cadena de frío y áreas críticas.",
  },
  {
    icon: <CheckCircle2 className="w-6 h-6 text-yellow-400" />,
    title: "Certificados digitales",
    desc: "Generación y almacenamiento de certificados de calibración y mantenimiento con firma digital.",
  },
];

const industries = [
  { icon: <HeartPulse className="w-8 h-8" />, label: "Clínicas & Hospitales", color: "text-rose-400" },
  { icon: <Microscope className="w-8 h-8" />, label: "Laboratorios", color: "text-cyan-400" },
  { icon: <Factory className="w-8 h-8" />, label: "Industria", color: "text-orange-400" },
  { icon: <Stethoscope className="w-8 h-8" />, label: "IPS & EPS", color: "text-emerald-400" },
  { icon: <Globe className="w-8 h-8" />, label: "Empresas de servicio", color: "text-violet-400" },
  { icon: <Building2 className="w-8 h-8" />, label: "Universidades", color: "text-blue-400" },
];

const steps = [
  { n: "01", title: "Registra tu empresa y equipos", desc: "Importa tu inventario masivamente o crea equipos uno a uno con todos sus datos técnicos." },
  { n: "02", title: "Configura cronogramas", desc: "Define frecuencias, responsables y tipos de mantenimiento. El sistema genera las órdenes automáticamente." },
  { n: "03", title: "Ejecuta y documenta", desc: "Técnicos reciben notificaciones, ejecutan con formularios digitales y adjuntan evidencias desde móvil o PC." },
  { n: "04", title: "Analiza y optimiza", desc: "Dashboards en tiempo real muestran el estado de tu flota, costos y oportunidades de mejora." },
];

/* ─── main component ─── */
const LandingPage = () => {
  const navigate = useNavigate();
  const statsRef = useRef(null);
  const statsInView = useInView(statsRef, { once: true, margin: "-100px" });

  const fadeUp = (delay = 0) => ({
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    transition: { duration: 0.6, delay },
    viewport: { once: true },
  });

  return (
    <div className="min-h-screen bg-gray-950 text-white font-sans overflow-x-hidden">

      {/* ── NAVBAR ── */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-gray-950/80 backdrop-blur-md border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <img src="/logo_white.png" alt="Previonix" className="h-10 w-auto" />
          <nav className="hidden md:flex items-center gap-8 text-sm text-gray-400">
            <a href="#modulos" className="hover:text-white transition">Módulos</a>
            <a href="#compliance" className="hover:text-white transition">Cumplimiento</a>
            <a href="#como-funciona" className="hover:text-white transition">Cómo funciona</a>
            <a href="#sectores" className="hover:text-white transition">Sectores</a>
          </nav>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/login")}
              className="text-sm text-gray-300 hover:text-white transition"
            >
              Iniciar sesión
            </button>
            <a
              href="mailto:contacto@previonix.com?subject=Solicitud de Demo"
              className="bg-cyan-500 hover:bg-cyan-400 text-gray-950 text-sm font-bold px-4 py-2 rounded-lg transition"
            >
              Solicitar Demo
            </a>
          </div>
        </div>
      </header>

      {/* ── HERO ── */}
      <section className="relative min-h-screen flex flex-col items-center justify-center text-center px-6 pt-24 overflow-hidden">
        {/* grid background */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "linear-gradient(rgba(6,182,212,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(6,182,212,0.4) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />
        {/* glow blobs */}
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-2/3 left-1/4 w-[300px] h-[300px] bg-violet-500/10 rounded-full blur-3xl pointer-events-none" />

        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative inline-flex items-center gap-2 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs font-semibold px-4 py-2 rounded-full mb-8"
        >
          <Zap className="w-3.5 h-3.5" /> Plataforma integral de mantenimiento industrial y biomédico
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="relative text-5xl md:text-7xl font-extrabold leading-tight max-w-5xl mb-6"
        >
          Gestiona, calibra y controla{" "}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-violet-400">
            cada equipo
          </span>{" "}
          con precisión total
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="relative text-lg md:text-xl text-gray-400 max-w-3xl mb-10"
        >
          Previonix unifica mantenimiento, soporte, calibración, inventario, costos y
          monitoreo IoT en una sola plataforma inteligente. Diseñada para ingeniería
          biomédica e industria.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="relative flex flex-col sm:flex-row gap-4 items-center"
        >
          <button
            onClick={() => navigate("/login")}
            className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-gray-950 font-bold px-8 py-4 rounded-xl text-base transition shadow-lg shadow-cyan-500/20"
          >
            <Rocket className="w-5 h-5" /> Acceder al sistema
          </button>
          <a
            href="mailto:contacto@previonix.com?subject=Solicitud de Demo"
            className="flex items-center gap-2 border border-white/20 hover:border-white/40 text-white px-8 py-4 rounded-xl text-base transition"
          >
            <Mail className="w-5 h-5" /> Solicitar una Demo
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="absolute bottom-10 flex flex-col items-center gap-1 text-gray-600 text-xs"
        >
          <ChevronDown className="w-5 h-5 animate-bounce" />
          Descubre todo lo que incluye
        </motion.div>
      </section>

      {/* ── STATS ── */}
      <section ref={statsRef} className="py-20 border-y border-white/5 bg-gray-900/40">
        <div className="max-w-5xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-10">
          <StatCard value={12} suffix="+" label="Módulos integrados" trigger={statsInView} />
          <StatCard value={100} suffix="%" label="Trazabilidad digital" trigger={statsInView} />
          <StatCard value={10} suffix="s" label="Actualización sensores IoT" trigger={statsInView} />
          <StatCard value={360} suffix="°" label="Visión de tu operación" trigger={statsInView} />
        </div>
      </section>

      {/* ── MÓDULOS ── */}
      <section id="modulos" className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fadeUp()} className="text-center mb-16">
            <span className="text-xs font-bold tracking-widest text-cyan-400 uppercase">Plataforma completa</span>
            <h2 className="text-4xl md:text-5xl font-extrabold mt-3 mb-4">Todo lo que necesitas, en un solo lugar</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Desde la orden de trabajo hasta el certificado de calibración: cada proceso
              de mantenimiento centralizado y automatizado.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {modules.map((m, i) => (
              <motion.div
                key={i}
                {...fadeUp(i * 0.04)}
                className="group relative bg-gray-900 border border-white/5 hover:border-white/20 rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl hover:shadow-black/40"
              >
                {/* gradient icon bg */}
                <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br ${m.color} mb-4 text-white`}>
                  {m.icon}
                </div>
                <span className="absolute top-4 right-4 text-[10px] font-bold tracking-wider text-gray-500 uppercase bg-white/5 px-2 py-0.5 rounded-full">
                  {m.badge}
                </span>
                <h3 className="text-base font-bold mb-2">{m.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-4">{m.desc}</p>
                <div className="flex flex-wrap gap-1.5">
                  {m.tags.map((t) => (
                    <span key={t} className="text-[10px] font-semibold text-gray-500 bg-white/5 px-2 py-0.5 rounded-full">
                      {t}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── COMPLIANCE BIOMÉDICO ── */}
      <section id="compliance" className="py-24 px-6 bg-gradient-to-br from-emerald-950/60 via-gray-900 to-gray-950 border-y border-emerald-500/10">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fadeUp()} className="text-center mb-16">
            <span className="text-xs font-bold tracking-widest text-emerald-400 uppercase">Cumplimiento normativo</span>
            <h2 className="text-4xl md:text-5xl font-extrabold mt-3 mb-4">
              Diseñado para cumplimiento{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">biomédico</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Previonix fue construido desde cero pensando en los requerimientos del sector
              salud: habilitación, trazabilidad metrológica, certificación y auditoría.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {complianceFeatures.map((f, i) => (
              <motion.div
                key={i}
                {...fadeUp(i * 0.08)}
                className="flex gap-4 bg-emerald-950/30 border border-emerald-500/15 rounded-2xl p-6 hover:border-emerald-500/30 transition"
              >
                <div className="mt-0.5 shrink-0">{f.icon}</div>
                <div>
                  <h3 className="font-bold mb-1">{f.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{f.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* highlight banner */}
          <motion.div
            {...fadeUp(0.3)}
            className="mt-12 flex flex-col md:flex-row items-center justify-between gap-6 bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border border-emerald-500/20 rounded-2xl p-8"
          >
            <div className="flex items-center gap-4">
              <ShieldCheck className="w-12 h-12 text-emerald-400 shrink-0" />
              <div>
                <p className="text-lg font-bold">Especialmente potente para ingeniería biomédica</p>
                <p className="text-gray-400 text-sm">
                  Hoja de vida de equipos, calibraciones con patrones ONAC, cronogramas regulatorios
                  y monitoreo ambiental continuo — todo en un solo sistema.
                </p>
              </div>
            </div>
            <a
              href="mailto:contacto@previonix.com?subject=Solicitud de Demo"
              className="shrink-0 flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-gray-950 font-bold px-6 py-3 rounded-xl transition whitespace-nowrap"
            >
              Hablar con un experto <ArrowRight className="w-4 h-4" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* ── IA ── */}
      <section className="py-24 px-6 bg-gradient-to-br from-gray-950 via-violet-950/30 to-gray-950">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          <motion.div {...fadeUp()}>
            <span className="text-xs font-bold tracking-widest text-violet-400 uppercase">Inteligencia Artificial</span>
            <h2 className="text-4xl md:text-5xl font-extrabold mt-3 mb-6">
              IA que trabaja{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">
                contigo
              </span>
            </h2>
            <p className="text-gray-400 mb-8 leading-relaxed">
              Nuestro motor de inteligencia artificial analiza el historial de cada equipo,
              detecta patrones de falla, sugiere acciones preventivas y genera reportes
              ejecutivos automáticamente. Menos tiempo analizando, más tiempo actuando.
            </p>
            <ul className="space-y-3">
              {[
                "Diagnóstico asistido de fallas técnicas",
                "Recomendaciones de mantenimiento predictivo",
                "Análisis de vida útil por equipo",
                "Generación automática de informes ejecutivos",
              ].map((item) => (
                <li key={item} className="flex items-center gap-3 text-sm text-gray-300">
                  <CheckCircle2 className="w-4 h-4 text-violet-400 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div {...fadeUp(0.2)} className="grid grid-cols-2 gap-4">
            {[
              { icon: <Brain className="w-6 h-6 text-violet-400" />, title: "Diagnóstico IA", value: "Análisis de fallas en segundos" },
              { icon: <TrendingUp className="w-6 h-6 text-pink-400" />, title: "Predictivo", value: "Anticipa fallos antes que ocurran" },
              { icon: <Activity className="w-6 h-6 text-cyan-400" />, title: "Monitoreo", value: "Alertas automáticas 24/7" },
              { icon: <FileText className="w-6 h-6 text-emerald-400" />, title: "Reportes auto", value: "Informes listos en un clic" },
            ].map((c, i) => (
              <div
                key={i}
                className="bg-gray-900 border border-white/5 rounded-2xl p-5 hover:border-violet-500/30 transition"
              >
                <div className="mb-3">{c.icon}</div>
                <p className="font-bold text-sm mb-1">{c.title}</p>
                <p className="text-gray-500 text-xs">{c.value}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── CÓMO FUNCIONA ── */}
      <section id="como-funciona" className="py-24 px-6 border-y border-white/5 bg-gray-900/30">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeUp()} className="text-center mb-16">
            <span className="text-xs font-bold tracking-widest text-cyan-400 uppercase">Proceso</span>
            <h2 className="text-4xl md:text-5xl font-extrabold mt-3 mb-4">¿Cómo funciona?</h2>
            <p className="text-gray-400">De la configuración inicial al análisis continuo, en cuatro pasos.</p>
          </motion.div>

          <div className="relative">
            {/* connecting line */}
            <div className="hidden md:block absolute top-8 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent" />
            <div className="grid md:grid-cols-4 gap-8">
              {steps.map((s, i) => (
                <motion.div key={i} {...fadeUp(i * 0.1)} className="text-center relative">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 font-extrabold text-xl mb-5">
                    {s.n}
                  </div>
                  <h3 className="font-bold mb-2">{s.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{s.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTORES ── */}
      <section id="sectores" className="py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeUp()} className="text-center mb-14">
            <span className="text-xs font-bold tracking-widest text-cyan-400 uppercase">Sectores</span>
            <h2 className="text-4xl md:text-5xl font-extrabold mt-3 mb-4">Para cualquier industria</h2>
            <p className="text-gray-400 max-w-xl mx-auto">
              Aunque con énfasis especial en biomédica, Previonix se adapta a cualquier
              organización que gestione equipos y procesos de mantenimiento.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {industries.map((ind, i) => (
              <motion.div
                key={i}
                {...fadeUp(i * 0.07)}
                className="flex flex-col items-center gap-3 bg-gray-900 border border-white/5 hover:border-white/15 rounded-2xl py-8 px-4 transition"
              >
                <span className={ind.color}>{ind.icon}</span>
                <span className="text-sm font-semibold text-gray-300">{ind.label}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA FINAL ── */}
      <section className="py-28 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-950/60 via-gray-900 to-violet-950/40" />
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage:
              "radial-gradient(circle, rgba(6,182,212,0.8) 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />
        <div className="relative max-w-3xl mx-auto text-center">
          <motion.div {...fadeUp()}>
            <img src="/logo_white.png" alt="Previonix" className="h-14 mx-auto mb-8 opacity-90" />
            <h2 className="text-4xl md:text-6xl font-extrabold mb-6">
              Transforma tu gestión{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-violet-400">
                hoy mismo
              </span>
            </h2>
            <p className="text-gray-400 text-lg mb-10">
              Únete a las organizaciones que ya gestionan su mantenimiento, calibraciones
              y soporte con Previonix. Sin papeles, sin retrasos, sin excusas.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate("/login")}
                className="flex items-center justify-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-gray-950 font-bold px-8 py-4 rounded-xl text-base transition shadow-lg shadow-cyan-500/25"
              >
                <Rocket className="w-5 h-5" /> Acceder al sistema
              </button>
              <a
                href="mailto:contacto@previonix.com?subject=Solicitud de Demo"
                className="flex items-center justify-center gap-2 border border-white/20 hover:border-white/40 bg-white/5 hover:bg-white/10 text-white px-8 py-4 rounded-xl text-base transition"
              >
                <Mail className="w-5 h-5" /> Solicitar Demo gratuita
              </a>
            </div>

            {/* trust badges */}
            <div className="mt-12 flex flex-wrap justify-center gap-6 text-gray-500 text-xs">
              {[
                { icon: <ShieldCheck className="w-4 h-4" />, text: "Datos seguros y cifrados" },
                { icon: <Star className="w-4 h-4" />, text: "Soporte personalizado" },
                { icon: <Globe className="w-4 h-4" />, text: "Acceso desde cualquier dispositivo" },
                { icon: <Users className="w-4 h-4" />, text: "Multi-usuario y multi-sede" },
              ].map((b, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  {b.icon} {b.text}
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="border-t border-white/5 bg-gray-950 py-10 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/favicon_dark.png" alt="" className="h-8 w-auto opacity-80" />
            <span className="text-gray-500 text-sm">
              © {new Date().getFullYear()} Previonix &mdash; By Creatimatica&reg;
            </span>
          </div>
          <div className="flex items-center gap-6 text-xs text-gray-600">
            <span>Gestión de mantenimiento</span>
            <span>·</span>
            <span>Calibraciones biomédicas</span>
            <span>·</span>
            <span>IoT & Analítica</span>
          </div>
          <a
            href="mailto:contacto@previonix.com"
            className="flex items-center gap-2 text-gray-500 hover:text-gray-300 text-sm transition"
          >
            <Mail className="w-4 h-4" /> contacto@previonix.com
          </a>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
